import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AreaChart as AreaChartIcon,
  BarChart3,
  Eye,
  EyeOff,
  LineChart as LineChartIcon,
  Palette,
  PieChart as PieChartIcon,
  Settings2,
  SlidersHorizontal,
} from "lucide-react";
import {
  REPORT_CHART_PREFS_KEY,
  DEFAULT_CHART_PREFS,
  buildChartDataset,
  buildScopedExecutiveDataset,
  resolveChartMetric,
} from "../../utils/reportChartData";
import { isChartMetricLicensed } from "../../utils/reportLicenses";
import { HelpTooltip } from "./UI";

export const CHART_TYPES = [
  { id: "bar", label: "Bar", icon: BarChart3 },
  { id: "line", label: "Line", icon: LineChartIcon },
  { id: "area", label: "Area", icon: AreaChartIcon },
  { id: "pie", label: "Pie", icon: PieChartIcon },
];

export const CHART_PALETTES = {
  ocean: {
    label: "Ocean",
    colors: ["#2563eb", "#0ea5e9", "#06b6d4", "#6366f1", "#64748b", "#14b8a6"],
    gradient: "from-blue-500 to-cyan-600",
  },
  indigo: {
    label: "Indigo",
    colors: ["#4f46e5", "#6366f1", "#818cf8", "#2563eb", "#0ea5e9", "#475569"],
    gradient: "from-indigo-500 to-blue-600",
  },
  teal: {
    label: "Teal",
    colors: ["#0d9488", "#14b8a6", "#2dd4bf", "#059669", "#0284c7", "#64748b"],
    gradient: "from-teal-500 to-cyan-600",
  },
  slate: {
    label: "Slate",
    colors: ["#334155", "#475569", "#64748b", "#94a3b8", "#0ea5e9", "#6366f1"],
    gradient: "from-slate-600 to-slate-400",
  },
};

export const CHART_METRICS = [
  { id: "auto", label: "Auto (scope-based)" },
  { id: "modules", label: "Module overview" },
  { id: "compliance", label: "Compliance & risk" },
  { id: "consent", label: "Consent breakdown" },
  { id: "cookie", label: "Cookie breakdown" },
  { id: "dsar", label: "DSAR status" },
  { id: "tprm", label: "TPRM status" },
  { id: "dspm", label: "DSPM inventory" },
  { id: "breach", label: "Breach incidents" },
  { id: "consent_variance", label: "Consent variance" },
  { id: "policy_store", label: "Policy store" },
  { id: "roadmap", label: "Compliance roadmap" },
];

const DEFAULT_PREFS = DEFAULT_CHART_PREFS;

const glassCard =
  "relative bg-white/55 backdrop-blur-xl rounded-2xl border border-white/60 shadow-lg ring-1 ring-slate-900/[0.05] overflow-hidden";

const glassInset =
  "bg-white/40 backdrop-blur-md rounded-xl border border-white/50 ring-1 ring-slate-900/[0.04]";

function loadPrefs() {
  try {
    const raw = localStorage.getItem(REPORT_CHART_PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_PREFS;
  }
}

function ChartTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/90 backdrop-blur-md border border-slate-200/80 rounded-xl px-3 py-2 shadow-lg text-xs">
      <p className="font-semibold text-slate-800">{label || payload[0]?.name}</p>
      <p className="text-slate-600 mt-0.5">
        {payload[0]?.value?.toLocaleString()}
        {unit}
      </p>
    </div>
  );
}

function ChartSkeleton({ height = 280 }) {
  return (
    <div className="animate-pulse flex flex-col gap-3" style={{ height }}>
      <div className="h-4 w-40 bg-slate-200/80 rounded-lg" />
      <div className="flex-1 bg-gradient-to-t from-slate-200/60 to-slate-100/40 rounded-xl" />
    </div>
  );
}

function EmptyChart({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-12 h-12 rounded-2xl bg-slate-100/80 flex items-center justify-center mb-3">
        <BarChart3 size={22} className="text-slate-400" />
      </div>
      <p className="text-sm font-medium text-slate-600">{message}</p>
      <p className="text-xs text-slate-400 mt-1">Try a different metric or data scope</p>
    </div>
  );
}

function ReportChartRenderer({ type, dataset, colors, prefs, height = 280 }) {
  const data = useMemo(
    () => dataset.data.map((d, i) => ({ ...d, fill: colors[i % colors.length] })),
    [dataset.data, colors],
  );

  const hasValues = data.some((d) => Number(d.value) > 0);
  if (!hasValues) return <EmptyChart message="No chart data for this selection" />;

  const common = {
    margin: { top: 8, right: 12, left: 0, bottom: 0 },
  };

  const tickStyle = { fontSize: 11, fill: "#64748b" };
  const legendProps = prefs.showLegend
    ? { verticalAlign: "bottom", height: 36, wrapperStyle: { fontSize: 11, color: "#475569" } }
    : null;

  if (type === "pie") {
    const filteredData = data.filter((d) => Number(d.value) > 0);
    const showLabels = Boolean(prefs.showLabels);
    const pieLegendProps = legendProps ? {
      ...legendProps,
      payload: data.map((d) => ({
        value: d.name,
        type: "square",
        id: d.name,
        color: d.fill,
      })),
    } : null;
    const pieMargin = showLabels
      ? { top: 40, right: 72, left: 72, bottom: 8 }
      : { top: 12, right: 16, left: 16, bottom: 8 };
    const outerR = Math.min(72, Math.max(52, height * 0.22));
    const innerR = Math.round(outerR * 0.62);

    return (
      <div className="w-full h-full overflow-visible px-1 pt-1 [&_.recharts-wrapper]:!overflow-visible [&_.recharts-surface]:overflow-visible">
        <ResponsiveContainer width="100%" height={height}>
          <PieChart margin={pieMargin} style={{ overflow: "visible" }}>
            <Pie
              data={filteredData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={innerR}
              outerRadius={outerR}
              paddingAngle={3}
              isAnimationActive={prefs.animate}
              label={
                showLabels
                  ? ({ name, percent }) => `${name} ${Math.round(percent * 100)}%`
                  : false
              }
              labelLine={showLabels ? { stroke: "#94a3b8", strokeWidth: 1 } : false}
            >
              {filteredData.map((entry, i) => (
                <Cell key={entry.name} fill={entry.fill || colors[i % colors.length]} stroke="rgba(255,255,255,0.8)" strokeWidth={2} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip unit={dataset.unit} />} />
            {pieLegendProps && <Legend {...pieLegendProps} />}
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  const ChartWrap = type === "line" ? LineChart : type === "area" ? AreaChart : BarChart;
  const Series = type === "line" ? Line : type === "area" ? Area : Bar;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ChartWrap {...common} data={data}>
        {prefs.showGrid && (
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.25)" vertical={false} />
        )}
        <XAxis dataKey="name" tick={tickStyle} axisLine={false} tickLine={false} interval={0} angle={data.length > 5 ? -18 : 0} textAnchor={data.length > 5 ? "end" : "middle"} height={data.length > 5 ? 52 : 30} />
        <YAxis tick={tickStyle} axisLine={false} tickLine={false} width={36} />
        <Tooltip content={<ChartTooltip unit={dataset.unit} />} />
        {legendProps && <Legend {...legendProps} />}
        <Series
          type="monotone"
          dataKey="value"
          isAnimationActive={prefs.animate}
          stroke={colors[0]}
          fill={type === "area" || type === "bar" ? colors[0] : undefined}
          fillOpacity={type === "area" ? 0.2 : type === "bar" ? 0.9 : undefined}
          radius={type === "bar" ? [8, 8, 0, 0] : undefined}
          strokeWidth={type === "line" ? 2.5 : undefined}
          dot={type === "line" ? { r: 4, fill: colors[0], strokeWidth: 0 } : false}
        >
          {type === "bar" &&
            data.map((entry, i) => (
              <Cell key={entry.name} fill={entry.fill || colors[i % colors.length]} />
            ))}
        </Series>
      </ChartWrap>
    </ResponsiveContainer>
  );
}

function ToggleChip({ active, onClick, children, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg transition-all duration-200 ${className} ${
        active
          ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/20"
          : "text-slate-600 hover:bg-white/60 hover:text-slate-900"
      }`}
    >
      {children}
    </button>
  );
}

function SwitchRow({ label, hint, checked, onChange }) {
  return (
    <label className="flex items-center justify-between gap-3 cursor-pointer group">
      <div className="min-w-0">
        <p className="text-xs font-medium text-slate-700">{label}</p>
        {hint && <p className="text-[10px] text-slate-500">{hint}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${
          checked ? "bg-blue-600" : "bg-slate-300/80"
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-5" : ""
          }`}
        />
      </button>
    </label>
  );
}

const REPORT_CHART_COPY = {
  executive: {
    title: "Leadership snapshot",
    subtitle: "Compliance score and open risks for the selected data scope.",
  },
  summary: {
    title: "Module comparison",
    subtitle: "One headline rate for each module in the selected data scope.",
  },
  detailed: {
    title: "Detailed breakdown",
    subtitle: "Distribution charts for the selected data scope. These settings are included in the PDF.",
  },
};

export default function ReportChartSection({
  reportData,
  dataSource,
  loading,
  showModule,
  reportType = "summary",
}) {
  const [prefs, setPrefs] = useState(loadPrefs);

  useEffect(() => {
    localStorage.setItem(REPORT_CHART_PREFS_KEY, JSON.stringify(prefs));
  }, [prefs]);

  const updatePref = useCallback((key, value) => {
    setPrefs((p) => ({ ...p, [key]: value }));
  }, []);

  const palette = CHART_PALETTES[prefs.palette] || CHART_PALETTES.ocean;
  const colors = palette.colors;
  const chartMetrics = useMemo(
    () => CHART_METRICS.filter((metric) => isChartMetricLicensed(metric.id, showModule)),
    [showModule],
  );

  const primaryMetric = resolveChartMetric(
    isChartMetricLicensed(prefs.primaryMetric, showModule) ? prefs.primaryMetric : "auto",
    dataSource,
  );
  const secondaryMetric = resolveChartMetric(
    isChartMetricLicensed(prefs.secondaryMetric, showModule) ? prefs.secondaryMetric : "auto",
    dataSource,
  );

  const primaryDataset = useMemo(
    () => buildChartDataset(primaryMetric, reportData, showModule),
    [primaryMetric, reportData, showModule],
  );

  const secondaryDataset = useMemo(() => {
    let resolved = secondaryMetric === primaryMetric && prefs.secondaryMetric === "auto"
      ? (primaryMetric === "modules" && isChartMetricLicensed("consent", showModule) ? "consent" : "modules")
      : secondaryMetric;
    if (!isChartMetricLicensed(resolved, showModule)) {
      resolved = "modules";
    }
    return buildChartDataset(resolved, reportData, showModule);
  }, [secondaryMetric, primaryMetric, prefs.secondaryMetric, reportData, showModule]);

  const fixedDataset = useMemo(() => {
    if (reportType === "executive") {
      return buildScopedExecutiveDataset(reportData, dataSource, showModule);
    }
    if (reportType === "summary") {
      return buildChartDataset("modules", reportData, showModule);
    }
    return null;
  }, [reportType, reportData, dataSource, showModule]);
  const chartCopy = REPORT_CHART_COPY[reportType] || REPORT_CHART_COPY.detailed;

  if (fixedDataset) {
    return (
      <div className={`${glassCard} p-4 sm:p-5 space-y-4`}>
        <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-400/40 to-transparent" />
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${palette.gradient} flex items-center justify-center shadow-md shadow-blue-500/15 ring-1 ring-white/30`}>
            <BarChart3 size={18} className="text-white" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-slate-900 font-display inline-flex items-center gap-1">
              {chartCopy.title}
              <HelpTooltip content={chartCopy.subtitle} size={13} position="bottom" maxWidth={300} />
            </h2>
            <p className="text-xs text-slate-500">{chartCopy.subtitle}</p>
          </div>
        </div>
        <div className={`${glassInset} p-4 flex flex-col overflow-visible min-h-[320px]`}>
          <div className="mb-3">
            <p className="text-sm font-semibold text-slate-800 inline-flex items-center gap-1">
              {fixedDataset.title}
              {fixedDataset.subtitle ? (
                <HelpTooltip content={fixedDataset.subtitle} size={12} position="bottom" />
              ) : null}
            </p>
            <p className="text-[10px] text-slate-500 mt-0.5">{fixedDataset.subtitle}</p>
          </div>
          <div className="flex-1 overflow-visible min-h-[260px]">
            {loading ? (
              <ChartSkeleton height={260} />
            ) : (
              <ReportChartRenderer
                type="bar"
                dataset={fixedDataset}
                colors={colors}
                prefs={{ ...prefs, showLegend: true }}
                height={260}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!prefs.visible) {
    return (
      <div className={`${glassCard} p-4 flex flex-wrap items-center justify-between gap-3`}>
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <BarChart3 size={16} className="text-blue-600" />
          <span className="inline-flex items-center gap-1">
            Visual analytics hidden
            <HelpTooltip content="Charts are hidden. Show them again to include the same charts in the PDF." size={13} position="bottom" />
          </span>
        </div>
        <button
          type="button"
          title="Show the charts for this report type."
          onClick={() => updatePref("visible", true)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50/80 border border-blue-200/60 rounded-xl px-3 py-2 hover:bg-blue-100/80 transition-colors"
        >
          <Eye size={14} />
          Show charts
        </button>
      </div>
    );
  }

  return (
    <div className={`${glassCard} p-4 sm:p-5 space-y-4`}>
      <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-400/40 to-transparent" />

      {/* Header + quick actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${palette.gradient} flex items-center justify-center shadow-md shadow-blue-500/15 ring-1 ring-white/30`}>
            <BarChart3 size={18} className="text-white" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-slate-900 font-display inline-flex items-center gap-1">
              {chartCopy.title}
              <HelpTooltip content={chartCopy.subtitle} size={13} position="bottom" maxWidth={300} />
            </h2>
            <p className="text-xs text-slate-500">{chartCopy.subtitle}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            title="Choose chart type, colours, and which figures to plot. These settings are included in the PDF."
            onClick={() => updatePref("panelOpen", !prefs.panelOpen)}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-xl px-3 py-2 transition-all ${
              prefs.panelOpen
                ? "bg-blue-600 text-white shadow-md shadow-blue-500/20"
                : "bg-white/50 text-slate-700 border border-white/60 hover:bg-white/70"
            }`}
          >
            <Settings2 size={14} />
            Customize
          </button>
          <button
            type="button"
            title="Hide charts on this page. They can be shown again."
            onClick={() => updatePref("visible", false)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-white/50 border border-white/60 rounded-xl px-3 py-2 hover:bg-white/70 transition-colors"
          >
            <EyeOff size={14} />
            <span className="hidden sm:inline">Hide</span>
          </button>
        </div>
      </div>

      {/* Customization panel */}
      {prefs.panelOpen && (
        <div className={`${glassInset} p-4 space-y-4 animate-fade-in`}>
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 uppercase tracking-wider">
            <SlidersHorizontal size={14} className="text-blue-600" />
            Chart options
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {/* Primary chart type */}
            <div className="space-y-2">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider inline-flex items-center gap-1">
                Primary chart
                <HelpTooltip content="Chart style for the first figure: bar, line, area, or pie." size={11} position="bottom" />
              </p>
              <div className="flex flex-wrap gap-1.5">
                {CHART_TYPES.map((t) => (
                  <ToggleChip key={t.id} active={prefs.primaryType === t.id} onClick={() => updatePref("primaryType", t.id)}>
                    <span className="inline-flex items-center gap-1">
                      <t.icon size={12} />
                      {t.label}
                    </span>
                  </ToggleChip>
                ))}
              </div>
            </div>

            {/* Secondary chart type */}
            <div className="space-y-2">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider inline-flex items-center gap-1">
                Secondary chart
                <HelpTooltip content="Chart style for the second figure." size={11} position="bottom" />
              </p>
              <div className="flex flex-wrap gap-1.5">
                {CHART_TYPES.map((t) => (
                  <ToggleChip key={t.id} active={prefs.secondaryType === t.id} onClick={() => updatePref("secondaryType", t.id)}>
                    <span className="inline-flex items-center gap-1">
                      <t.icon size={12} />
                      {t.label}
                    </span>
                  </ToggleChip>
                ))}
              </div>
            </div>

            {/* Color palette */}
            <div className="space-y-2">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <Palette size={12} />
                Color theme
                <HelpTooltip content="Colours used in both charts and in the downloaded PDF." size={11} position="bottom" />
              </p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(CHART_PALETTES).map(([id, p]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => updatePref("palette", id)}
                    title={p.label}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl border transition-all ${
                      prefs.palette === id
                        ? "border-blue-400 bg-blue-50/60 ring-2 ring-blue-400/30"
                        : "border-white/60 bg-white/40 hover:bg-white/60"
                    }`}
                  >
                    <span className="flex -space-x-1">
                      {p.colors.slice(0, 4).map((c) => (
                        <span key={c} className="w-3 h-3 rounded-full ring-2 ring-white" style={{ background: c }} />
                      ))}
                    </span>
                    <span className="text-[10px] font-semibold text-slate-600">{p.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Metrics */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider inline-flex items-center gap-1">
                Primary data
                <HelpTooltip content="The figure plotted in the first chart." size={11} position="bottom" />
              </label>
              <select
                value={prefs.primaryMetric}
                onChange={(e) => updatePref("primaryMetric", e.target.value)}
                className="w-full text-xs rounded-xl border border-white/60 bg-white/60 backdrop-blur-sm px-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
              >
                {chartMetrics.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider inline-flex items-center gap-1">
                Secondary data
                <HelpTooltip content="The figure plotted in the second chart." size={11} position="bottom" />
              </label>
              <select
                value={prefs.secondaryMetric}
                onChange={(e) => updatePref("secondaryMetric", e.target.value)}
                className="w-full text-xs rounded-xl border border-white/60 bg-white/60 backdrop-blur-sm px-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
              >
                {chartMetrics.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </div>

            {/* Toggles */}
            <div className="space-y-3 md:col-span-2 xl:col-span-1">
              <SwitchRow label="Show legend" hint="Names each colour in the chart." checked={prefs.showLegend} onChange={(v) => updatePref("showLegend", v)} />
              <SwitchRow label="Show grid" hint="Draws guide lines behind bar, line, and area charts." checked={prefs.showGrid} onChange={(v) => updatePref("showGrid", v)} />
              <SwitchRow label="Pie labels" checked={prefs.showLabels} onChange={(v) => updatePref("showLabels", v)} hint="Shows names on pie and donut charts." />
              <SwitchRow label="Animate" hint="Charts draw in when the page loads." checked={prefs.animate} onChange={(v) => updatePref("animate", v)} />
            </div>
          </div>
        </div>
      )}

      {/* Chart grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[primaryDataset, secondaryDataset].map((dataset, idx) => {
          const chartType = idx === 0 ? prefs.primaryType : prefs.secondaryType;
          const isPie = chartType === "pie";
          const chartHeight = isPie ? 340 : 260;
          return (
            <div
              key={idx}
              className={`${glassInset} p-4 flex flex-col overflow-visible ${
                isPie ? "min-h-[420px]" : "min-h-[320px]"
              }`}
            >
              <div className="mb-3">
                <p className="text-sm font-semibold text-slate-800 inline-flex items-center gap-1">
                  {dataset.title}
                  {dataset.subtitle ? (
                    <HelpTooltip content={dataset.subtitle} size={12} position="bottom" />
                  ) : null}
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5">{dataset.subtitle}</p>
              </div>
              <div className={`flex-1 overflow-visible ${isPie ? "min-h-[340px]" : "min-h-[260px]"}`}>
                {loading ? (
                  <ChartSkeleton height={chartHeight} />
                ) : (
                  <ReportChartRenderer
                    type={chartType}
                    dataset={dataset}
                    colors={colors}
                    prefs={prefs}
                    height={chartHeight}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
